import classNames from 'classnames';
import React, { cloneElement, HTMLAttributes, ReactElement, useEffect, useRef } from 'react';

import tooltip from '@uswds/uswds/packages/usa-tooltip/src';

enum Positions {
  top = 'top',
  left = 'left',
  bottom = 'bottom',
  right = 'right',
}

type Position = keyof typeof Positions;

export interface TooltipProps {
  children: React.ReactElement<TooltipChildElementProps>;
  position?: Position;
  label?: string;
}

export interface TooltipChildElementProps extends HTMLAttributes<'html'> {
  title?: string;
  'data-position'?: Position;
}

/**
 *  Provides access to the USWDS tooltip code to manually configure your own tooltips.
 *
 *  Children of the `parentRef` should have at a minimum:
 *  - `className` attribute with value of `Tooltip.className`
 *  - `title` attribute with the text content of the tooltip
 *  - optional `data-position` attribute with a value `Tooltip.position.<top|right|left|bottom>`
 *
 * @param parentRef parent html element to all of the children configured to have tooltips
 */
export const useTooltip = (parentRef: React.RefObject<HTMLElement> | null): void => {
  useEffect(() => {
    const tooltipParent = parentRef?.current;
    if (tooltipParent) {
      tooltip.on(tooltipParent);
    }
  }, [parentRef]);
};

const tooltipClassname = 'usa-tooltip';

const Tooltip = ({ children, label, position }: TooltipProps): ReactElement => {
  const parentRef = useRef<HTMLSpanElement>(null);
  useTooltip(parentRef);

  return (
    <span ref={parentRef}>
      {cloneElement(
        children,
        {
          title: label ?? children.props.title,
          'data-position': position ?? children.props['data-position'] ?? 'top',
          className: classNames(children.props.className, tooltipClassname),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        children.props ? children.props.children : null,
      )}
    </span>
  );
};

Tooltip.className = tooltipClassname;
Tooltip.positions = Positions;

export default Tooltip;
